import { useLingui } from '@lingui/react';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import TeamImageSection from '../components/TeamImageSection';
import { apiBaseUrl } from '../config';

import type { ImageUrls } from '../types/media';
import {
  JobGroup,
  JobLevel,
  JobTitle,
  JobTitlesByGroup,
  type TeamRole,
} from '../types/team';
import { getFileExtension, uploadImage, uploadJson } from '../utils/fileUpload';

export default function TeamEditPage() {
  const { i18n } = useLingui();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [role, setRole] = useState<TeamRole | null>(null);
  const [expirationDate, setExpirationDate] = useState('');
  const [imageUrls, setImageUrls] = useState<ImageUrls | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const initialSnapshot = useRef<string>('');
  const [publishState, setPublishState] = useState<{
    status: 'idle' | 'loading' | 'success' | 'error';
    message?: string;
  }>({ status: 'idle' });

  useEffect(() => {
    if (!id) return;
    loadMember(id);
  }, [id]);

  async function loadMember(memberId: string): Promise<void> {
    try {
      const { data } = await axios.get(
        `${apiBaseUrl}/files/team/${memberId}.json`,
      );
      setName(data.name ?? '');
      setRole(data.role ?? null);
      setExpirationDate(data.expirationDate ?? '');
      setImageUrls(data.image ?? null);
      initialSnapshot.current = JSON.stringify({
        name: data.name ?? '',
        role: data.role ?? null,
        expirationDate: data.expirationDate ?? '',
        image: data.image ?? null,
      });
    } catch (err) {
      console.error('Fehler beim Laden', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!initialSnapshot.current) return;
    const current = JSON.stringify({
      name,
      role,
      expirationDate,
      image: imageUrls,
    });
    setIsDirty(current !== initialSnapshot.current);
  }, [name, role, expirationDate, imageUrls]);

  const today = new Date().toISOString().split('T')[0];
  const isValid =
    name.trim() !== '' &&
    role !== null &&
    imageUrls !== null &&
   expirationDate >= today;

  async function handleImageUpload(file: File): Promise<void> {
    setImageUploading(true);
    try {
      const filename = `team/${crypto.randomUUID()}.${getFileExtension(file.name)}`;
      setImageUrls(await uploadImage(filename, file));
    } catch (err) {
      console.error('Bild-Upload fehlgeschlagen', err);
    } finally {
      setImageUploading(false);
    }
  }

  async function handleSave(): Promise<void> {
    setPublishState({ status: 'loading' });
    try {
      const member = {
        id,
        name,
        role,
        image: imageUrls,
        expirationDate,
        updatedAt: new Date().toISOString(),
      };

      await uploadJson(`team/${id}.json`, member);

      setPublishState({ status: 'success' });
    } catch (err) {
      setPublishState({
        status: 'error',
        message: err instanceof Error ? err.message : 'Unbekannter Fehler',
      });
    }
  }

  const availableJobTitles = role?.jobGroup
    ? JobTitlesByGroup[role.jobGroup]
    : Object.values(JobTitle);

  if (loading) {
    return (
      <div className="container-page padding-top-8">
        <p>{i18n._('common.loading')}</p>
      </div>
    );
  }

  const isLoading = publishState.status === 'loading';

  return (
    <div className="container-page padding-top-8 padding-bottom-8">
      <div className="col-xs-12 col-lg-6">
        <div className="flex flex-col gap-2 w-full">
          <h1>{i18n._('team.edit')}</h1>

          <TeamImageSection
            onUpload={handleImageUpload}
            imageUrls={imageUrls}
            imageUploading={imageUploading}
          />

          <div className="border border-gray-tint-6 rounded-_20 padding-3 w-full">
            <h3>{i18n._('team.name')}</h3>
            <Input
              disabled={isLoading}
              value={name}
              onChange={(v) => setName(v ?? '')}
              placeholder={i18n._('team.namePlaceholder')}
            />
          </div>

          <div className="border border-gray-tint-6 rounded-_20 padding-3 w-full">
            <h3>{i18n._('team.group')}</h3>
            <Select
              disabled={isLoading}
              placeholder={i18n._('team.groupPlaceholder')}
              value={role?.jobGroup ?? null}
              onChange={(groupValue) => {
                if (!groupValue) return;
                const newJobGroup = groupValue as JobGroup;
                setRole((prev) => ({
                  level: prev?.level ?? JobLevel.APPRENTICE,
                  jobGroup: newJobGroup,
                  jobTitle: JobTitlesByGroup[newJobGroup][0],
                }));
              }}
              options={[
                { label: 'Management-Team', value: JobGroup.MANAGEMENT },
                { label: 'Designer', value: JobGroup.DESIGNER },
                { label: 'Developer', value: JobGroup.DEVELOPER },
                { label: 'Social-Media Team', value: JobGroup.SOCIAL_MEDIA },
              ]}
            />
          </div>

          <div className="border border-gray-tint-6 rounded-_20 padding-3 w-full">
            <h3>{i18n._('team.level')}</h3>
            <Select
              disabled={isLoading}
              placeholder={i18n._('team.levelPlaceholder')}
              value={role?.level ?? null}
              onChange={(levelValue) => {
                if (!levelValue) return;
                setRole((prev) => ({
                  level: levelValue as JobLevel,
                  jobGroup: prev?.jobGroup ?? JobGroup.DEVELOPER,
                  jobTitle: prev?.jobTitle ?? JobTitle.BACKEND,
                }));
              }}
              options={[
                { label: 'Apprentice', value: JobLevel.APPRENTICE },
                {
                  label: 'Young Professional',
                  value: JobLevel.YOUNG_PROFESSIONAL,
                },
                { label: 'Professional', value: JobLevel.PROFESSIONAL },
                { label: 'Management', value: JobLevel.MANAGEMENT },
              ]}
            />
          </div>

          
            <div className="border border-gray-tint-6 rounded-_20 padding-3 w-full">
              <h3>{i18n._('team.expirationDate')}</h3>
              <Input
                disabled={isLoading}
                type="date"
                value={expirationDate}
                onChange={(v) => setExpirationDate(v ?? '')}
                placeholder={i18n._('team.expirationDatePlaceholder')}
                minDate='today'
              />
            </div>

          <div className="border border-gray-tint-6 rounded-_20 padding-3 w-full">
            <h3>{i18n._('team.role')}</h3>
            <Select
              disabled={isLoading}
              placeholder={i18n._('team.rolePlaceholder')}
              value={role?.jobTitle ?? null}
              onChange={(titleValue) => {
                if (!titleValue) return;
                setRole((prev) => ({
                  level: prev?.level ?? JobLevel.APPRENTICE,
                  jobGroup: prev?.jobGroup ?? JobGroup.DEVELOPER,
                  jobTitle: titleValue as JobTitle,
                }));
              }}
              options={availableJobTitles.map((title) => ({
                label: title,
                value: title,
              }))}
            />
          </div>

          <div className="flex gap-2 margin-top-2">
            <Button
              theme="secondary"
              disabled={isLoading}
              onClick={() => navigate('/team')}
            >
              {i18n._('common.back')}
            </Button>
            <Button onClick={handleSave} disabled={!isValid || isLoading}>
              {isLoading ? i18n._('common.saving') : i18n._('common.save')}
            </Button>
          </div>

          {publishState.status === 'success' && (
            <p style={{ color: 'green' }}>{i18n._('team.updateSuccess')}</p>
          )}
          {publishState.status === 'error' && (
            <p style={{ color: 'red' }}>
              {i18n._('team.saveError', { message: publishState.message })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
